/**
 * Mega Menu Header - JavaScript
 */

class MegaMenuHeader {
  constructor(container) {
    this.container = container;
    this.isDesktop = window.innerWidth >= 1024;
    
    // Cache DOM elements
    this.elements = {
      primaryItems: container.querySelectorAll('[data-primary-item]'),
      primaryTriggers: container.querySelectorAll('[data-primary-trigger]'),
      secondaryBar: container.querySelector('[data-secondary-bar]'),
      secondaryNav: container.querySelector('[data-secondary-nav]'),
      secondaryItems: container.querySelectorAll('[data-secondary-item]'),
      secondaryTriggers: container.querySelectorAll('[data-secondary-trigger]'),
      megaPanels: container.querySelector('[data-mega-panels]'),
      panels: container.querySelectorAll('[data-panel-for]'),
      overlay: container.querySelector('[data-header-overlay]'),
      
      // Mobile elements
      mobileDrawer: container.querySelector('[data-mobile-drawer]'),
      mobileDropdownTrigger: container.querySelector('[data-mobile-dropdown-trigger]'),
      mobileDropdownLabel: container.querySelector('[data-mobile-dropdown-label]'),
      mobileDropdown: container.querySelector('[data-mobile-dropdown]'),
      mobileDropdownItems: container.querySelectorAll('[data-mobile-dropdown-item]'),
      mobileContent: container.querySelector('[data-mobile-content]'),
      mobilePanels: container.querySelectorAll('[data-mobile-panel]')
    };
    
    // State
    this.state = {
      activePrimaryId: null,
      activeSecondaryId: null,
      activePanelId: null,
      mobileOpen: false,
      mobileSubmenuStack: []
    };
    
    this.init();
  }
  
  init() {
    this.bindDesktopEvents();
    this.bindMobileEvents();
    this.bindResizeHandler();
    this.initializeDefaultState();
  }
  
  // =========================================================================
  // Desktop Interactions
  // =========================================================================
  
  bindDesktopEvents() {
    // Primary nav items
    this.elements.primaryItems.forEach(item => {
      const hasSecondary = item.dataset.hasSecondary === 'true';
      const trigger = item.querySelector('[data-primary-trigger]');
      
      if (hasSecondary) {
        if (trigger) {
          trigger.addEventListener('click', (e) => {
            e.preventDefault();
            this.handlePrimaryClick(item);
          });
        }
        
        item.addEventListener('mouseenter', () => {
          if (this.isDesktop) {
            this.handlePrimaryClick(item);
          }
        });
      } else {
        if (trigger) {
          item.addEventListener('mouseenter', () => {
            if (this.isDesktop) {
              this.showPanel(item.dataset.primaryItem, 'primary');
            }
          });
          
          item.addEventListener('mouseleave', () => {
            if (this.isDesktop) {
              this.hideAllPanels();
            }
          });
        }
      }
    });
    
    // Secondary nav items
    this.elements.secondaryTriggers.forEach(trigger => {
      const item = trigger.closest('[data-secondary-item]');
      
      item.addEventListener('mouseenter', () => {
        if (this.isDesktop) {
          this.handleSecondaryHover(item);
        }
      });
      
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleSecondaryHover(item);
      });
    });
    
    // Close panels when leaving the mega menu area
    if (this.elements.secondaryBar) {
      let closeTimeout;
      
      const startCloseTimer = () => {
        closeTimeout = setTimeout(() => {
          this.hideAllPanels();
        }, 150);
      };
      
      const cancelCloseTimer = () => {
        clearTimeout(closeTimeout);
      };
      
      this.elements.secondaryBar.addEventListener('mouseleave', startCloseTimer);
      this.elements.secondaryBar.addEventListener('mouseenter', cancelCloseTimer);
      
      if (this.elements.megaPanels) {
        this.elements.megaPanels.addEventListener('mouseleave', startCloseTimer);
        this.elements.megaPanels.addEventListener('mouseenter', cancelCloseTimer);
      }
    }
    
    // Overlay click
    if (this.elements.overlay) {
      this.elements.overlay.addEventListener('click', () => {
        this.closeAll();
      });
    }
    
    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeAll();
      }
    });
  }
  
  handlePrimaryClick(item) {
    const itemId = item.dataset.primaryItem;
    const hasSecondary = item.dataset.hasSecondary === 'true';
    
    this.clearActiveStates();
    
    item.classList.add('is-active');
    this.state.activePrimaryId = itemId;
    
    if (hasSecondary) {
      this.showSecondaryNavFor(itemId);
      
      const firstSecondary = this.container.querySelector(`[data-secondary-item][data-parent="${itemId}"]`);
      if (firstSecondary) {
        this.handleSecondaryHover(firstSecondary);
      }
    }
  }
  
  handleSecondaryHover(item) {
    const itemId = item.dataset.secondaryItem;
    const megaType = item.querySelector('[data-mega-type]')?.dataset.megaType;
    
    this.elements.secondaryItems.forEach(i => i.classList.remove('is-active'));
    
    item.classList.add('is-active');
    this.state.activeSecondaryId = itemId;
    
    if (megaType && megaType !== 'none') {
      this.showPanel(itemId, 'secondary');
    } else {
      this.hideAllPanels();
    }
  }
  
  showSecondaryNavFor(parentId) {
    this.elements.secondaryItems.forEach(item => {
      if (item.dataset.parent === parentId) {
        item.classList.add('is-visible');
      } else {
        item.classList.remove('is-visible');
      }
    });
  }
  
  showPanel(id, type) {
    this.elements.panels.forEach(panel => {
      panel.classList.remove('is-active');
      panel.setAttribute('aria-hidden', 'true');
    });
    
    const panel = this.container.querySelector(`[data-panel-for="${id}"]`);
    if (panel) {
      panel.classList.add('is-active');
      panel.setAttribute('aria-hidden', 'false');
      this.state.activePanelId = id;
    }
  }
  
  hideAllPanels() {
    this.elements.panels.forEach(panel => {
      panel.classList.remove('is-active');
      panel.setAttribute('aria-hidden', 'true');
    });
    this.state.activePanelId = null;
  }
  
  clearActiveStates() {
    this.elements.primaryItems.forEach(item => item.classList.remove('is-active'));
    this.elements.secondaryItems.forEach(item => {
      item.classList.remove('is-active');
      item.classList.remove('is-visible');
    });
    this.hideAllPanels();
    this.state.activePrimaryId = null;
    this.state.activeSecondaryId = null;
  }
  
  closeAll() {
    this.clearActiveStates();
    this.closeMobileDrawer();
    
    if (this.elements.overlay) {
      this.elements.overlay.classList.remove('is-visible');
    }
    
    this.initializeDefaultState();
  }
  
  initializeDefaultState() {
    if (!this.isDesktop) return;

    const currentPath = window.location.pathname;

    // Try to find a matching primary item based on secondary item links
    let matchedPrimaryId = null;

    const secondaryItems = this.container.querySelectorAll('[data-secondary-item]');
    secondaryItems.forEach(item => {
      const link = item.querySelector('a');
      if (link && link.getAttribute('href') && link.getAttribute('href') === currentPath) {
        matchedPrimaryId = item.dataset.parent;
      }
    });

    // Fall back to first primary item with secondary nav
    if (!matchedPrimaryId) {
      const firstWithSecondary = this.container.querySelector('[data-primary-item][data-has-secondary="true"]');
      if (firstWithSecondary) {
        matchedPrimaryId = firstWithSecondary.dataset.primaryItem;
      }
    }

    if (matchedPrimaryId) {
      const primaryItem = this.container.querySelector(`[data-primary-item="${matchedPrimaryId}"]`);
      if (primaryItem) {
        primaryItem.classList.add('is-active');
        this.state.activePrimaryId = matchedPrimaryId;
        this.showSecondaryNavFor(matchedPrimaryId);
      }

      // Also highlight the matching secondary item
      secondaryItems.forEach(item => {
        const link = item.querySelector('a');
        if (link && link.getAttribute('href') === currentPath) {
          item.classList.add('is-active');
          this.state.activeSecondaryId = item.dataset.secondaryItem;
        }
      });
    }
  }
  
  // =========================================================================
  // Mobile Interactions
  // =========================================================================
  
  bindMobileEvents() {
    // Mobile toggle buttons (open and close)
    const mobileToggles = this.container.querySelectorAll('[data-mobile-toggle]');
    mobileToggles.forEach(toggle => {
      toggle.addEventListener('click', () => {
        this.toggleMobileDrawer();
      });
    });
    
    // Mobile custom dropdown
    if (this.elements.mobileDropdownTrigger) {
      this.elements.mobileDropdownTrigger.addEventListener('click', () => {
        this.toggleMobileDropdown();
      });
    }

    if (this.elements.mobileDropdownItems.length > 0) {
      this.elements.mobileDropdownItems.forEach(item => {
        item.addEventListener('click', () => {
          this.handleMobileDropdownSelect(item);
        });
      });
      
      // Initialize first item
      const firstItem = this.elements.mobileDropdownItems[0];
      if (firstItem) {
        this.handleMobileDropdownSelect(firstItem, false);
      }
    }

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => {
      if (this.elements.mobileDropdown && 
          this.elements.mobileDropdownTrigger &&
          !this.elements.mobileDropdownTrigger.contains(e.target) &&
          !this.elements.mobileDropdown.contains(e.target)) {
        this.closeMobileDropdown();
      }
    });
    
    // Bind submenu triggers
    this.bindMobileSubmenus();
  }
  
  bindMobileSubmenus() {
    // Submenu triggers (slide right)
    const triggers = this.container.querySelectorAll('[data-mobile-submenu-trigger]');
    triggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        const submenuId = trigger.dataset.mobileSubmenuTrigger;
        this.openMobileSubmenu(submenuId);
      });
    });
    
    // Back buttons
    const backButtons = this.container.querySelectorAll('[data-mobile-back]');
    backButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.closeMobileSubmenu();
      });
    });
    
    // Accordion triggers (expand/collapse)
    const accordionTriggers = this.container.querySelectorAll('[data-mobile-accordion-trigger]');
    accordionTriggers.forEach(trigger => {
      trigger.addEventListener('click', () => {
        this.toggleMobileAccordion(trigger);
      });
    });
  }

  toggleMobileAccordion(trigger) {
    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
    const content = trigger.nextElementSibling;
    
    if (isExpanded) {
      trigger.setAttribute('aria-expanded', 'false');
      content.classList.remove('is-open');
    } else {
      trigger.setAttribute('aria-expanded', 'true');
      content.classList.add('is-open');
    }
   const mobileScrollArea = this.container.querySelector('.header-mega__mobile-content');
  if (mobileScrollArea) {
    mobileScrollArea.style.overflow = 'hidden';
    void mobileScrollArea.offsetHeight;
    mobileScrollArea.style.overflow = '';
  }
  }
  
  toggleMobileDrawer() {
    if (this.state.mobileOpen) {
      this.closeMobileDrawer();
    } else {
      this.openMobileDrawer();
    }
  }
  
  openMobileDrawer() {
    this.state.mobileOpen = true;
    
    if (this.elements.mobileDrawer) {
      this.elements.mobileDrawer.setAttribute('aria-hidden', 'false');
    }
    
    if (this.elements.overlay) {
      this.elements.overlay.classList.add('is-visible');
    }
    
    document.body.classList.add('header-mega-drawer-open');
  }
  
  closeMobileDrawer() {
    this.state.mobileOpen = false;
    
    if (this.elements.mobileDrawer) {
      this.elements.mobileDrawer.setAttribute('aria-hidden', 'true');
    }
    
    if (this.elements.overlay) {
      this.elements.overlay.classList.remove('is-visible');
    }
    
    document.body.classList.remove('header-mega-drawer-open');
    
    // Reset submenu state
    this.closeAllMobileSubmenus();
  }
  
  toggleMobileDropdown() {
    if (this.elements.mobileDropdown) {
      this.elements.mobileDropdown.classList.toggle('is-open');
    }
  }

  closeMobileDropdown() {
    if (this.elements.mobileDropdown) {
      this.elements.mobileDropdown.classList.remove('is-open');
    }
  }

  handleMobileDropdownSelect(item, closeDropdown = true) {
    const itemId = item.dataset.mobileDropdownItem;
    const hasSecondary = item.dataset.hasSecondary === 'true';
    
    // Update active state
    this.elements.mobileDropdownItems.forEach(i => i.classList.remove('is-active'));
    item.classList.add('is-active');
    
    // Update label
    if (this.elements.mobileDropdownLabel) {
      this.elements.mobileDropdownLabel.textContent = item.textContent.trim();
    }
    
    // Close dropdown
    if (closeDropdown) {
      this.closeMobileDropdown();
    }
    
    // Show appropriate panel
    this.showMobilePanel(itemId);
  }

  showMobilePanel(panelId) {
    // Hide all mobile panels
    this.elements.mobilePanels.forEach(panel => {
      panel.classList.remove('is-active');
      panel.setAttribute('aria-hidden', 'true');
    });
    
    // Show the selected panel
    const panel = this.container.querySelector(`[data-mobile-panel="${panelId}"]`);
    if (panel) {
      panel.classList.add('is-active');
      panel.setAttribute('aria-hidden', 'false');
    }
    
    // Reset any open submenus
    this.closeAllMobileSubmenus();
  }
  
  openMobileSubmenu(submenuId) {
    const submenu = this.container.querySelector(`[data-mobile-submenu="${submenuId}"]`);
    if (submenu) {
      submenu.setAttribute('aria-hidden', 'false');
      this.state.mobileSubmenuStack.push(submenuId);
    }
  }
  
  closeMobileSubmenu() {
    const lastId = this.state.mobileSubmenuStack.pop();
    if (lastId) {
      const submenu = this.container.querySelector(`[data-mobile-submenu="${lastId}"]`);
      if (submenu) {
        submenu.setAttribute('aria-hidden', 'true');
      }
    }
  }
  
  closeAllMobileSubmenus() {
    const submenus = this.container.querySelectorAll('[data-mobile-submenu]');
    submenus.forEach(submenu => {
      submenu.setAttribute('aria-hidden', 'true');
    });
    this.state.mobileSubmenuStack = [];
  }
  
  // =========================================================================
  // Resize Handler
  // =========================================================================
  
  bindResizeHandler() {
    let resizeTimer;
    
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const wasDesktop = this.isDesktop;
        this.isDesktop = window.innerWidth >= 1024;
        
        if (wasDesktop !== this.isDesktop) {
          this.closeAll();
        }
      }, 100);
    });
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  const headerContainer = document.querySelector('[data-header-mega]');
  if (headerContainer) {
    new MegaMenuHeader(headerContainer);
  }
});